import { Injectable, OnDestroy } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { Store } from "@ngrx/store";
import { DialogService } from "@obg/mat.ui.core";
import {
	AuthSelector,
	CORE_ROUTE,
	CoreState,
	CustomerManagementSelector,
	MessageService,
	MessageType,
	MULTI_DEPOSIT_LIMIT_MESSAGE_CONTEXT,
} from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { RouteSelector } from "@obg/ngx.router";
import { combineLatest, EMPTY, merge, Observable, Subject, Subscription } from "rxjs";
import { filter, map, tap, withLatestFrom } from "rxjs/operators";

import { LoginUiFlowConfigService } from "../login-ui/login-ui-flow.config";
import { LoginUiFlowService } from "../login-ui/login-ui-flow.service";
import { LoginUiConfigService } from "../login.config";
import { NewLoginFlowStep } from "../login.model";
import { WelcomeFlowService } from "../welcome-flow/welcome-flow.service";

import { LoginFlowContainer } from "./containers/login-flow/login-flow.container";
import { LOGIN_FLOW_DIALOG_ID } from "./containers/login-flow/login-flow.dialog";
import { LoginFlowLoaderService } from "./login-flow-loader.service";
import { MgaFlowSteps } from "./login-flow.const";
import { NewFlowDialogModel } from "./new-flow-dialog.model";
import { StepperService } from "./stepper.service";

@Injectable({ providedIn: "root" })
export class NewFlowService implements OnDestroy {
	private data$$: Subscription | undefined;
	private shouldCloseLoginModal$ = new Subject<void>();

	constructor(
		private store: Store<CoreState>,
		private loginUiFlowService: LoginUiFlowService,
		private deviceConfigService: DeviceConfigService,
		private dialogService: DialogService,
		private loginUiFlowConfigService: LoginUiFlowConfigService,
		private loginUiConfigService: LoginUiConfigService,
		private loginFlowLoaderService: LoginFlowLoaderService,
		private matDialog: MatDialog,
		private stepperService: StepperService,
		private messageService: MessageService,
		private welcomeFlowService: WelcomeFlowService
	) {}

	initialize() {
		if (this.data$$) {
			return;
		}

		this.data$$ = merge(
			this.gdprConsents$(),
			this.isAuthenticated$(),
			this.termsAndConditions$(),
			this.depositLimit$(),
			this.isFirstLogin$()
		).subscribe();
	}

	get closeLoginModal$(): Observable<unknown> {
		return this.shouldCloseLoginModal$.asObservable();
	}

	getNewLoginFlowSteps(): NewLoginFlowStep[] {
		return this.loginUiConfigService.getNewLoginFlowSteps();
	}

	isAuthenticated$(): Observable<boolean> {
		return this.store
			.select(AuthSelector.isAuthenticated)
			.pipe(tap((isAuth) => (isAuth ? this.shouldCloseLoginModal$.next() : EMPTY)));
	}

	isFirstLogin$(): Observable<void> {
		return this.isAuthenticated$().pipe(
			withLatestFrom(this.store.select(AuthSelector.isFirstLogin)),
			filter(([isAuth, isFirstlogin]) => isAuth && isFirstlogin === false),
			tap(() => this.stepperService.navigate(false)),
			map(() => undefined)
		);
	}

	depositLimit$(): Observable<void> {
		return combineLatest([
			this.messageService.consume$({
				context: MULTI_DEPOSIT_LIMIT_MESSAGE_CONTEXT.update,
				messageType: MessageType.Success,
			}),
			this.store.select(CustomerManagementSelector.getAllowedAction),
		]).pipe(
			filter(([, allowedActions]) => !!allowedActions?.deposit.isAllowed),
			tap(() => this.stepperService.navigate(true)),
			map(() => undefined)
		);
	}

	gdprConsents$(): Observable<void> {
		return this.loginUiFlowService.shouldShowConsents$().pipe(
			withLatestFrom(this.store.pipe(RouteSelector.getName$), this.store.select(AuthSelector.isAuthenticated)),
			tap(([, currentRoute, isAuth]) => {
				this.shouldCloseLoginModal$.next();
				if (isAuth) {
					return;
				}

				if (this.isLoginFlowDialogOpen()) {
					this.stepperService.navigate(true);
				} else {
					this.openMgaDialog(MgaFlowSteps.Consents, currentRoute);
				}
			}),
			map(() => undefined)
		);
	}

	termsAndConditions$(): Observable<void> {
		return this.loginUiFlowService.detectTermsAndConditions$().pipe(
			tap(() => {
				this.shouldCloseLoginModal$.next();
				this.openMgaDialog(MgaFlowSteps.TermsAndConditions);
			}),
			map(() => undefined)
		);
	}

	declineTermsAndConditions$(): Observable<void> {
		return this.loginUiFlowService
			.declineTermsAndConditions$()
			.pipe(tap(() => this.stepperService.navigate(false)));
	}

	unsubscribe() {
		this.data$$?.unsubscribe();
	}

	ngOnDestroy() {
		this.unsubscribe();
	}

	openMgaDialog(flowStartIndex: number, currentRoute?: string) {
		this.welcomeFlowService.triggerABTestEvent();

		const isNavbarVisible =
			!this.deviceConfigService.isDesktop() && this.loginUiFlowConfigService.showHeaderAndFooterForNDCflow();

		const data: NewFlowDialogModel = {
			navbar: isNavbarVisible,
			navbarOptions: {
				hideBackButton: true,
				showCloseButton: currentRoute === CORE_ROUTE.auth.login,
				preventDefaultBack: true,
			},
			hideClose: true,
			hideFooter: !isNavbarVisible,
			title: isNavbarVisible ? "login-consents.caption.generic" : undefined,
			minHeight: "240px",
			flowStartIndex,
		};

		this.dialogService.openComponent(LoginFlowContainer, {
			disableClose: true,
			data,
			minHeight: "240px",
			minWidth: this.deviceConfigService.isDesktop() ? "800px" : "auto",
			autoFocus: false,
			id: LOGIN_FLOW_DIALOG_ID,
		});

		this.loginFlowLoaderService.toggleLoading(true);
	}

	isLoginFlowDialogOpen(): boolean {
		return !!this.matDialog.openDialogs.find((dialog) => dialog.id === LOGIN_FLOW_DIALOG_ID);
	}
}
